import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ZButton from "@/components/ZButton";
import { ArrowLeft, BellRing, Mail, ShieldCheck, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const BUYER_KEYS = [
  { key: "buyer_purchase_confirmation", label: "Confirmation d'achat de billet", desc: "Reçu et QR code envoyés par e-mail après chaque achat." },
  { key: "buyer_event_reminder_d1", label: "Rappel la veille de l'événement (J-1)", desc: "Recevez un e-mail 24h avant chaque événement." },
  { key: "buyer_event_reminder_d0", label: "Rappel le jour de l'événement", desc: "Un dernier rappel le matin même." },
  { key: "buyer_event_changed", label: "Modification d'un événement réservé", desc: "Date, lieu ou annulation : on vous prévient." },
  { key: "buyer_voucher_received", label: "Bon d'achat reçu", desc: "Un commerçant vous a envoyé un bon." },
  { key: "buyer_fundraiser_milestone", label: "Étape franchie sur une cagnotte que vous suivez", desc: "25%, 50%, 75%, 100% de l'objectif." },
];

const ORG_KEYS = [
  { key: "org_new_sale", label: "Nouvelle vente de billet", desc: "Notification dès qu'un participant achète un billet." },
  { key: "org_new_contribution", label: "Nouvelle contribution à une cagnotte", desc: "On vous prévient pour chaque don." },
  { key: "org_new_vote", label: "Nouveau vote dans un scrutin", desc: "Un participant vient de voter." },
  { key: "org_new_survey", label: "Nouvelle réponse à un sondage", desc: "Une réponse vient d'arriver." },
  { key: "org_soldout", label: "Événement complet (sold-out)", desc: "Toutes les places ont été vendues." },
  { key: "org_event_moderation", label: "Décision de modération (approbation/rejet)", desc: "L'admin a approuvé ou refusé votre événement." },
  { key: "org_waitlist_signup", label: "Inscription liste d'attente", desc: "Quelqu'un veut être prévenu si une place se libère." },
];

const ADMIN_KEYS = [
  { key: "admin_new_event_pending", label: "Nouvel événement à modérer", desc: "Un organisateur a publié un événement en attente." },
  { key: "admin_new_organizer", label: "Nouvel organisateur inscrit", desc: "Un compte organisateur vient d'être créé." },
];

const SMS_KEYS = [
  { key: "buyer_sms_purchase_confirmation", label: "Confirmation d'achat par SMS", desc: "Recevez un SMS dès qu'un billet est confirmé (numéro requis)." },
  { key: "buyer_sms_event_reminder", label: "Rappels d'événement par SMS", desc: "Un SMS la veille et le jour J de vos événements." },
  { key: "buyer_sms_waitlist", label: "Liste d'attente par SMS", desc: "Soyez alerté par SMS dès qu'une place se libère." },
];

function Toggle({ checked, onChange, testId }) {
  return (
    <label style={{ position: "relative", display: "inline-block", width: 44, height: 24, cursor: "pointer" }} data-testid={testId}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} />
      <span style={{
        position: "absolute", inset: 0, background: checked ? "var(--z-teal)" : "#ccc", borderRadius: 24, transition: "background .2s",
      }} />
      <span style={{
        position: "absolute", top: 3, left: checked ? 23 : 3,
        width: 18, height: 18, background: "white", borderRadius: "50%",
        transition: "left .2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
      }} />
    </label>
  );
}

function Section({ icon, title, prefKeys, prefs, onChange }) {
  return (
    <section style={{ marginBottom: 26 }}>
      <h2 style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 16, fontWeight: 700, margin: "0 0 12px", color: "var(--z-text)" }}>
        {icon} {title}
      </h2>
      <div className="z-card" style={{ padding: 0 }}>
        {prefKeys.map((p, i) => (
          <div key={p.key} style={{
            display: "flex", alignItems: "center", gap: 16, padding: "14px 16px",
            borderTop: i === 0 ? "none" : "1px solid var(--z-border)",
          }} data-testid={`notif-row-${p.key}`}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{p.label}</div>
              <div style={{ fontSize: 12, color: "var(--z-muted)", marginTop: 2 }}>{p.desc}</div>
            </div>
            <Toggle
              checked={prefs[p.key] !== false}
              onChange={(v) => onChange(p.key, v)}
              testId={`notif-toggle-${p.key}`}
            />
          </div>
        ))}
      </div>
    </section>
  );
}

export default function NotificationPrefsPage() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    api.get("/me/notification-prefs")
      .then((r) => setPrefs(r.data))
      .finally(() => setLoading(false));
  }, []);

  const onChange = (key, value) => {
    setPrefs((p) => ({ ...p, [key]: value }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/me/notification-prefs", prefs);
      setPrefs(r.data);
      setDirty(false);
      toast.success("Préférences enregistrées");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !prefs) {
    return <div className="z-container" style={{ textAlign: "center" }}><span className="z-loader z-loader-dark" /></div>;
  }

  const role = user?.role || "buyer";
  const showOrg = role === "organizer" || role === "admin";
  const showAdmin = role === "admin";
  const backLink = role === "organizer" ? "/org/profile" : "/profile";

  return (
    <div className="z-container" style={{ maxWidth: 720 }} data-testid="notif-prefs-page">
      <Link to={backLink} className="z-btn z-btn-ghost z-btn-sm" data-testid="notif-back-link"><ArrowLeft size={14} /> Retour au profil</Link>
      <h1 className="z-page-title" style={{ marginTop: 14 }}>
        <Mail size={22} style={{ verticalAlign: "middle", marginRight: 8 }} />
        Notifications
      </h1>
      <p className="z-page-sub">Choisissez les e-mails et SMS que vous souhaitez recevoir. Les SMS nécessitent un numéro de téléphone valide. Vous pouvez modifier ces préférences à tout moment.</p>

      <Section icon={<BellRing size={16} color="var(--z-teal)" />} title="En tant qu'utilisateur" prefKeys={BUYER_KEYS} prefs={prefs} onChange={onChange} />
      <Section icon={<MessageSquare size={16} color="var(--z-orange)" />} title="Notifications par SMS" prefKeys={SMS_KEYS} prefs={prefs} onChange={onChange} />
      {showOrg && (
        <Section icon={<BellRing size={16} color="var(--z-orange)" />} title="En tant qu'organisateur" prefKeys={ORG_KEYS} prefs={prefs} onChange={onChange} />
      )}
      {showAdmin && (
        <Section icon={<ShieldCheck size={16} color="var(--z-teal)" />} title="En tant qu'administrateur" prefKeys={ADMIN_KEYS} prefs={prefs} onChange={onChange} />
      )}

      <div style={{ position: "sticky", bottom: 20, marginTop: 24 }}>
        <ZButton
          onClick={save}
          loading={saving}
          disabled={!dirty}
          block
          variant={dirty ? "primary" : "outline"}
          data-testid="notif-save-btn"
        >
          {dirty ? "Enregistrer les modifications" : "Tout est à jour"}
        </ZButton>
      </div>
    </div>
  );
}
